import { el, btn } from '@/ui/dom';
import type { Game } from '@/core/Game';
import {
  DEMO_RELEASE_STEER,
  DEMO_RELEASE_THROTTLE,
  demoCoachLevel,
  demoFrozen,
  demoPopup,
  demoPopupStep,
  demoPraise,
  detectDemoDevice,
  initialDemoState,
  releaseDemoStep,
  tickDemo,
  type DemoDevice,
  type DemoObs,
  type DemoState,
  type DemoStepId,
} from '@/ui/onboarding/demoRaceContent';
import { hapticImpact, hapticTick } from '@/ui/haptics';
import { sfxCollect, sfxUiTick } from '@/audio/sfx';

/**
 * DemoRaceDirector.ts (M112) — the LIVE half of the onboarding demo race.
 *
 * Flow drops the fresh Racer ID into a solo NYC race and hands this director
 * the wheel: it rides Game's per-frame hook, freezes the sim under each
 * teaching popup (demoRaceContent owns the step machine + copy), peeks input
 * NON-destructively to detect the taught action, and pins the first collected
 * item to BOOST (a solo racer's roll table can never produce one naturally —
 * lead-tier weights have boost 0, measured in powerups.rollItem).
 *
 * The freeze mechanics that matter (all measured against the input stack):
 *  - While frozen, Game.update returns before input.read(), so the fire tap
 *    latch and key edges stay ARMED for the resume frame.
 *  - A use-item KEY already held when the FIRE popup froze things would
 *    swallow the resumed rising edge — rearmItemEdge() clears that trap.
 *  - Release thresholds sit well past the joystick deadzone so a resting
 *    thumb can't dismiss a lesson.
 *
 * When the fired boost's run ends, onComplete() hands Flow the transition to
 * the unlock video. SKIP (chip or Escape) hands it onSkip() → the finale.
 */

export interface DemoRaceDirectorCallbacks {
  onComplete(): void;
  onSkip(): void;
}

/** The linger after the boost completes — let the surge land, then hand off. */
const OUTRO_MS = 1100;
/** How long a praise pop stays up (matches the CSS animation). */
const PRAISE_MS = 1500;
/** Every frozen popup holds at least this long before its release action is
 *  accepted — a player already holding the throttle (or mashing) would
 *  otherwise melt the freeze the same frame and never get a reading beat. */
const MIN_FREEZE_MS = 1000;

export class DemoRaceDirector {
  private readonly root: HTMLDivElement;
  private readonly popupHost: HTMLDivElement;
  private readonly praiseEl: HTMLDivElement;
  private state: DemoState = initialDemoState();
  private readonly device: DemoDevice;
  private shownPopup: DemoStepId | null = null;
  /** M115: the coach level the CURRENT popup was rendered for. The FIRE card's
   *  action line escalates in place, so the popup has to re-render on the flip
   *  as well as on a step change. */
  private shownCoach: 0 | 1 | 2 = 0;
  private disposed = false;
  private ended = false;
  private outroTimer: number | null = null;
  private praiseTimer: number | null = null;
  /** The reading-beat gate: no release before this timestamp (MIN_FREEZE_MS). */
  private freezeGateUntil = 0;

  private readonly onKey = (e: KeyboardEvent): void => {
    if (this.disposed) return;
    if (e.key === 'Escape') {
      this.finish('skip');
      return;
    }
    this.maybeReleaseRule();
  };

  private readonly onPointer = (): void => {
    if (this.disposed) return;
    this.maybeReleaseRule();
  };

  /** Pause-on-blur still fires offline (the pause SCREEN is suppressed by
   *  Flow's demo guard) — resume silently when the tab comes back. */
  private readonly onVisibility = (): void => {
    if (this.disposed || document.hidden) return;
    this.game.resume();
  };

  constructor(
    private readonly game: Game,
    parent: HTMLElement,
    private readonly cb: DemoRaceDirectorCallbacks,
  ) {
    this.device = detectDemoDevice(
      window.matchMedia('(pointer: coarse)').matches,
      Array.from(navigator.getGamepads?.() ?? []).some((p) => p !== null),
    );

    this.root = el('div', 'demo-overlay', parent);
    btn('SKIP', 'ob-skip demo-skip', this.root, () => this.finish('skip'));
    this.popupHost = el('div', 'demo-popup-host', this.root);
    this.praiseEl = el('div', 'demo-praise', this.root);

    // Hides the dead HUD pause button + the solo "1st / 1" badge (ui.css).
    document.body.classList.add('demo-tutorial');

    window.addEventListener('keydown', this.onKey);
    window.addEventListener('pointerdown', this.onPointer);
    document.addEventListener('visibilitychange', this.onVisibility);

    this.game.frameHook = (frameDt) => this.tick(frameDt);
    this.game.setTutorialFreeze(true);
    this.renderPopup(); // stamps the first reading beat
  }

  /** Flow's raceFinished guard routes here: driving the whole course to the
   *  line IS completing the demo — straight to the unlock video. */
  handleRaceFinished(): void {
    this.finish('complete');
  }

  private maybeReleaseRule(): void {
    if (this.state.step !== 'rule') return;
    if (performance.now() < this.freezeGateUntil) return; // the reading beat
    this.applyRelease();
  }

  private applyRelease(): void {
    const prev = this.state;
    this.state = releaseDemoStep(prev);
    if (this.state !== prev) {
      hapticTick();
      sfxUiTick();
      this.syncFreeze();
      this.renderPopup();
    }
  }

  private tick(frameDt: number): void {
    if (this.disposed || this.ended) return;
    const access = this.game.tutorialAccess;
    if (!access) return;
    const player = access.race.player;

    // 1) Frozen popups watch for their release action — but never before the
    //    reading beat has passed (a held throttle would melt the freeze the
    //    same frame the popup appears and the lesson would flash by unread).
    if (demoFrozen(this.state) && performance.now() >= this.freezeGateUntil) {
      const peek = this.game.peekInput();
      if (this.state.step === 'use') {
        if (peek.itemQueued || peek.itemHeld) {
          // A key still held from before the freeze would swallow the resumed
          // rising edge — re-arm so the first unfrozen read() fires the boost.
          // (The touch tap latch needs nothing: peek left it queued.)
          if (peek.itemHeld) this.game.rearmItemEdge();
          this.applyRelease();
        }
      } else if (this.state.step !== 'rule') {
        if (
          Math.abs(peek.throttle) > DEMO_RELEASE_THROTTLE ||
          Math.abs(peek.steer) > DEMO_RELEASE_STEER
        ) {
          this.applyRelease();
        }
      } else if (this.anyGamepadButton()) {
        this.maybeReleaseRule();
      }
    }

    // 2) Pin the roll: whatever a collected box rolled becomes BOOST (the
    //    taught item; solo rolls literally cannot produce one). Overwriting
    //    mid-roulette is invisible — the flicker reveals the final `held`.
    const pw = player.powerups;
    if (pw.held !== null && pw.held !== 'boost' && pw.activeKind === null) {
      pw.held = 'boost';
    }

    // 3) Observe + advance the step machine.
    const car = player.car.state;
    const ball = player.ball.state;
    const obs: DemoObs = {
      carSpeed: player.car.speed,
      ballDist: Math.hypot(car.x - ball.x, car.z - ball.z),
      ballSpeed: Math.hypot(ball.vx, ball.vz),
      ballArc: player.ballDistance(access.track),
      itemSettled: pw.held !== null && pw.roulette <= 0,
      itemActive: pw.activeKind !== null,
      finished: player.finished,
    };
    const prev = this.state;
    this.state = tickDemo(prev, obs, frameDt);
    if (this.state.step !== prev.step) this.onStepChange(prev.step, this.state.step);

    // 4) Freeze truth + popup + the FIRE coach pulse follow the state.
    this.syncFreeze();
    const coach = demoCoachLevel(this.state);
    if (demoPopupStep(this.state) !== this.shownPopup || coach !== this.shownCoach) {
      this.renderPopup();
    }
    this.game.setItemCoach(coach);
  }

  private anyGamepadButton(): boolean {
    for (const p of navigator.getGamepads?.() ?? []) {
      if (p && p.buttons.some((b) => b.pressed)) return true;
    }
    return false;
  }

  private onStepChange(from: DemoStepId, to: DemoStepId): void {
    // M115: the FIRE lesson is live, so the fire goes through the normal
    // destructive read() — which only sees a RISING edge. A player already
    // holding the fire key/button as the lesson arms would otherwise have a
    // dead control forever. Re-arming makes the held press count on the next
    // frame: a fast lesson beats an unresponsive one.
    if (to === 'use') this.game.rearmItemEdge();
    const praise = demoPraise(from, to);
    if (praise) {
      this.showPraise(praise);
      hapticImpact();
      sfxCollect();
    }
    if (to === 'done' && this.outroTimer === null) {
      // Let the boost's surge land, then hand Flow the unlock-video swap.
      this.outroTimer = window.setTimeout(() => this.finish('complete'), OUTRO_MS);
    }
  }

  private syncFreeze(): void {
    this.game.setTutorialFreeze(demoFrozen(this.state));
  }

  private showPraise(text: string): void {
    this.praiseEl.textContent = text;
    this.praiseEl.classList.remove('on');
    void this.praiseEl.offsetWidth; // restart the pop
    this.praiseEl.classList.add('on');
    if (this.praiseTimer !== null) window.clearTimeout(this.praiseTimer);
    this.praiseTimer = window.setTimeout(() => {
      this.praiseEl.classList.remove('on');
      this.praiseTimer = null;
    }, PRAISE_MS);
  }

  // ---- Popup DOM -------------------------------------------------------------

  private renderPopup(): void {
    const step = demoPopupStep(this.state);
    const coach = demoCoachLevel(this.state);
    this.shownPopup = step;
    this.shownCoach = coach;
    this.popupHost.replaceChildren();
    // The live FIRE banner hugs the top edge (see .demo-popup-live in ui.css).
    this.popupHost.classList.toggle('demo-popup-host-live', step === 'use');
    // A fresh FROZEN popup re-arms the reading beat (see MIN_FREEZE_MS).
    if (demoFrozen(this.state)) {
      this.freezeGateUntil = performance.now() + MIN_FREEZE_MS;
    }
    if (step === null) return;
    const p = demoPopup(step, this.device, coach === 2);
    if (!p) return;

    const card = el('div', 'demo-popup', this.popupHost);
    if (coach === 2) card.classList.add('demo-popup-urgent');
    // Owner 2026-09-16: the FIRE lesson runs LIVE (M115) and its full card hid
    // the road while the boost was firing. The live step wears a compact
    // one-row banner instead (ui.css .demo-popup-live) — same copy, a third
    // of the height, the world stays visible under it.
    if (step === 'use') card.classList.add('demo-popup-live');
    const kicker = el('div', 'lb-kicker', card);
    el('span', 'mhead-rule', kicker);
    el('span', 'lb-kicker-text', kicker).textContent = p.kicker;
    el('span', 'mhead-rule', kicker);
    if (step === 'use') {
      // Two rows only: [mock] HEADLINE · sub, then the pulsing action line.
      const row = el('div', 'demo-popup-liverow', card);
      if (p.mock) this.renderMock(row, p.mock);
      const h = el('div', 'demo-popup-headline', row);
      h.textContent = p.headline;
      h.dataset.text = p.headline;
      el('div', 'demo-popup-sub', row).textContent = p.sub;
      if (p.action) el('div', 'demo-popup-action', card).textContent = p.action;
      return;
    }
    const h = el('div', 'demo-popup-headline', card);
    h.textContent = p.headline;
    h.dataset.text = p.headline;
    el('div', 'demo-popup-sub', card).textContent = p.sub;
    if (p.mock) this.renderMock(card, p.mock);
    if (p.action) el('div', 'demo-popup-action', card).textContent = p.action;
  }

  /** Small device-true visual aids beside the copy (pure CSS constructs). */
  private renderMock(
    card: HTMLElement,
    mock: NonNullable<ReturnType<typeof demoPopup>>['mock'],
  ): void {
    const wrap = el('div', 'demo-mock', card);
    if (mock === 'joystick') {
      const base = el('div', 'demo-mock-joy', wrap);
      el('div', 'demo-mock-knob', base);
    } else if (mock === 'arrows') {
      const grid = el('div', 'demo-mock-keys', wrap);
      el('span', 'demo-key demo-key-up', grid).textContent = '▲';
      el('span', 'demo-key', grid).textContent = '◀';
      el('span', 'demo-key', grid).textContent = '▼';
      el('span', 'demo-key', grid).textContent = '▶';
      el('div', 'demo-mock-note', wrap).textContent = 'OR WASD';
    } else if (mock === 'stick') {
      const base = el('div', 'demo-mock-joy demo-mock-pad', wrap);
      el('div', 'demo-mock-knob', base);
      el('div', 'demo-mock-note', wrap).textContent = 'LEFT STICK';
    } else if (mock === 'itembtn') {
      el('div', 'demo-mock-item', wrap).textContent = '?';
    } else if (mock === 'space') {
      el('span', 'demo-key demo-key-space', wrap).textContent = 'SPACE';
    } else if (mock === 'abtn') {
      el('div', 'demo-mock-abtn', wrap).textContent = 'A';
    }
  }

  // ---- Teardown --------------------------------------------------------------

  private finish(how: 'complete' | 'skip'): void {
    if (this.ended) return;
    this.ended = true;
    if (how === 'complete') this.cb.onComplete();
    else this.cb.onSkip();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.outroTimer !== null) window.clearTimeout(this.outroTimer);
    if (this.praiseTimer !== null) window.clearTimeout(this.praiseTimer);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('pointerdown', this.onPointer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.game.frameHook = null;
    this.game.setTutorialFreeze(false);
    this.game.setItemCoach(0);
    document.body.classList.remove('demo-tutorial');
    this.root.remove();
  }
}
